import { ApiError, request } from "@/lib/api"
import { z } from "zod"
import {
    BoletoGeradoSchema,
    CompetenciasSchema,
    type Repasse,
    RepasseSchema,
    UploadSchema,
} from "./schema"

// O CNPJ do empregador sai do token, no authorizer — nunca da rota. É o que impede trocar o
// identificador na URL e enxergar outra empresa. O mês, sim, vem da URL, e o serviço confere que
// ele pertence a este CNPJ antes de responder.
const BASE = "/condo-loan-async/employer/payroll-reconciliation"

// c= do seletor, r= dos e-mails, e sem nenhum dos dois o mês aberto. Fora do formato não entra no
// caminho: um ".." subiria de rota levando o token.
export const referenciaDaBusca = (busca: URLSearchParams): string | null => {
    const referencia = busca.get("c")
    return referencia && /^\d{4}-(0[1-9]|1[0-2])$/.test(referencia) ? referencia : null
}

export type Acao = "alegar" | "pagar"

export const acaoDaBusca = (busca: URLSearchParams): Acao | null => {
    const acao = busca.get("acao")
    return acao === "alegar" || acao === "pagar" ? acao : null
}

export const caminhoDoRepasse = (busca: URLSearchParams): string => {
    const referencia = referenciaDaBusca(busca)
    if (referencia) return `${BASE}/competence/${referencia}`
    const id = busca.get("r")
    if (id && /^[a-f0-9]{24}$/i.test(id)) return `${BASE}/${id}`
    return `${BASE}/active`
}

export const fetchRepasse = async (caminho: string): Promise<Repasse> =>
    request(caminho, RepasseSchema)

export const fetchCompetencias = async () => request(BASE, CompetenciasSchema)

// O Content-Type do PUT tem de ser o que o serviço assinou, e ele assina pela extensão.
const TIPOS: Record<string, string> = {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
}

type Evidencia = "REPORT" | "PAYMENT_PROOF"

const subir = async (id: string, arquivo: File, kind: Evidencia) => {
    const { upload_url, file_path } = await request(
        `${BASE}/${id}/payment-claim/upload-url`,
        UploadSchema,
        { method: "POST", body: JSON.stringify({ file_name: arquivo.name }) },
    )
    const extensao = arquivo.name.split(".").pop()?.toLowerCase() ?? ""
    // Direto para o S3 e sem o token: a URL assinada é a autorização.
    const resposta = await fetch(upload_url, {
        method: "PUT",
        body: arquivo,
        headers: { "Content-Type": TIPOS[extensao] ?? "" },
    }).catch(() => null)
    if (!resposta) throw new ApiError("rede")
    if (!resposta.ok) throw new ApiError("servidor", resposta.status)
    return { file_path, kind }
}

export interface EnvioDeAlegacao {
    relatorio: File
    comprovantes: File[]
}

// O serviço alega todos os contratos alegáveis do mês; a tela busca o repasse de novo depois.
export const enviarAlegacao = async (id: string, envio: EnvioDeAlegacao) => {
    const evidence = await Promise.all([
        subir(id, envio.relatorio, "REPORT"),
        ...envio.comprovantes.map((arquivo) => subir(id, arquivo, "PAYMENT_PROOF")),
    ])
    await request(`${BASE}/${id}/payment-claim`, z.unknown(), {
        method: "POST",
        body: JSON.stringify({ evidence }),
    })
}

export const gerarBoleto = async (id: string, parcelas: string[]) => {
    const { boleto } = await request(`${BASE}/${id}/bankslip`, BoletoGeradoSchema, {
        method: "POST",
        body: JSON.stringify({ installment_ids: parcelas }),
    })
    return boleto
}
